import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/auth/services/auth.service';
import { ViewAsSessionService } from '@core/auth/view-as/view-as-session.service';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, shareReplay, switchMap, tap } from 'rxjs/operators';
import { DataStorageService } from '../auth/services/data-storage.service';

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {
  private static readonly AUTH_ENDPOINT_SUFFIX: string = 'api/authenticate';
  private static readonly VIEW_AS_READONLY_MESSAGE: string = 'viewas.readonly';

  private refreshInFlight$: Observable<unknown> | null = null;

  constructor(
    private dataStorage: DataStorageService,
    private auth: AuthService,
    private router: Router,
    private viewAsSession: ViewAsSessionService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const normalizedRequest: HttpRequest<unknown> = request.clone({ url: this.normalizeUrl(request.url) });
    const authedRequest: HttpRequest<unknown> = this.attachAuthHeaders(normalizedRequest);

    return next
      .handle(authedRequest)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err, authedRequest, next)));
  }

  private handleError(
    err: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.isBlockedViewAsAction(err)) {
      this.viewAsSession.showBlockedActionNotice();
      // Swallow so the block surfaces only as the in-place notice. Rethrowing would let
      // each feature's error handler open the generic error dialog with the raw key, and
      // those live in lazy modules with their own MatDialog, so it can't be suppressed centrally.
      return EMPTY;
    }

    // A view-as session has no refresh token, so a failing call to the auth endpoint (e.g. the
    // guard's doomed refresh attempt once the JWT's exp passes) must end the view-as session
    // rather than fall through to logout()->/login, which a view-as tab has no way to use.
    if (this.dataStorage.isViewAsSession() && (this.isAuthEndpoint(err.url) || err.status === 401)) {
      this.viewAsSession.handleSessionEnded();
      return throwError(() => err);
    }

    if (this.isAuthEndpoint(err.url)) {
      this.logout();
      return throwError(() => err);
    }

    if (err.status === 401) {
      return this.handleUnauthorized(request, next);
    }

    if (!request.headers.get('skip') && this.auth.isUserAuthorized()) {
      this.navigateToChallenges();
    }
    return throwError(() => err);
  }

  private isBlockedViewAsAction(err: HttpErrorResponse): boolean {
    // gate on the real session state, not the message alone — a stray viewas.readonly body
    // in a normal session must surface as a regular error, not be swallowed
    return (
      this.dataStorage.isViewAsSession() &&
      err.status === 403 &&
      this.extractErrorMessage(err) === HttpInterceptorService.VIEW_AS_READONLY_MESSAGE
    );
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const body: unknown = err.error;
    if (body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string') {
      return (body as { message: string }).message;
    }
    return '';
  }

  private handleUnauthorized(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.auth.refreshSession().pipe(
        tap({
          complete: () => {
            this.refreshInFlight$ = null;
          },
          error: () => {
            this.refreshInFlight$ = null;
            this.logout();
          }
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this.refreshInFlight$.pipe(
      switchMap(() => next.handle(this.attachAuthHeaders(request))),
      catchError((err: HttpErrorResponse) => {
        if (err?.status === 401) {
          this.logout();
        }
        return throwError(() => err);
      })
    );
  }

  private attachAuthHeaders(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const token: string = this.dataStorage.getToken();
    const totpToken: string = this.dataStorage.getTotpToken();
    let headers: HttpHeaders = request.headers;
    if (token && !this.isAuthEndpoint(request.url)) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    if (totpToken) {
      headers = headers.set('AC-Totp', totpToken);
    }
    return request.clone({ headers });
  }

  private isAuthEndpoint(url: string | null | undefined): boolean {
    return !!url && url.endsWith(HttpInterceptorService.AUTH_ENDPOINT_SUFFIX);
  }

  private navigateToChallenges(): void {
    // Stay put on the community surfaces: the community-header iframe forwards every
    // NavigationStart to the Bettermode host (which navigates the top window), and on the
    // portal /community page a background-call failure must not pull the member out of
    // the embedded community.
    if (this.router.url.startsWith('/community')) {
      return;
    }
    // Recover to the challenges list directly, not '/home' — that route re-runs the
    // landing-page redirect, which ejects PORTAL_LAND_PAGE=TRIBE members to /community.
    // setTimeout keeps throwError visible to upstream subscribers.
    setTimeout(() => {
      this.router.navigate(['/home/challenges']);
    });
  }

  private logout(): void {
    // setTimeout keeps throwError visible to upstream subscribers.
    setTimeout(() => {
      this.auth.logout();
    });
  }

  private normalizeUrl(url: string): string {
    if (!url || url === '/') {
      return url;
    }
    const searchIndex: number = url.indexOf('?');
    const hashIndex: number = url.indexOf('#');
    let cutIndex = -1;
    if (searchIndex >= 0 && hashIndex >= 0) {
      cutIndex = Math.min(searchIndex, hashIndex);
    } else if (searchIndex >= 0) {
      cutIndex = searchIndex;
    } else if (hashIndex >= 0) {
      cutIndex = hashIndex;
    }
    const path: string = cutIndex >= 0 ? url.substring(0, cutIndex) : url;
    const suffix: string = cutIndex >= 0 ? url.substring(cutIndex) : '';
    const normalizedPath: string = path.length > 1 ? path.replace(/\/+$/, '') : path;
    return normalizedPath + suffix;
  }
}
